import { useState, useRef, useCallback } from "react";
import { streamChatV2, ChatV2Event } from "@/lib/chatV2Transport";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content?: string;
  parts: any[];
  createdAt?: Date;
}

export interface ToolActivity {
  id: string;
  toolCallId: string;
  toolName: string;
  args?: any;
  result?: any;
  error?: string;
  status: "pending" | "completed" | "error";
}

export interface UseChatV2Options {
  apiUrl: string;
  threadId?: string;
  assistantInstanceId?: string;
  organizationId?: string;
  userId?: string;
  model?: string;
  initialMessages?: ChatMessage[];
  onFinish?: (message: ChatMessage, usage?: any) => void;
  onError?: (error: Error) => void;
}

export interface SendMessageOptions {
  content: string;
  parts?: any[];
  toolSelections?: string[];
  inputData?: any;
}

export function useChatV2(options: UseChatV2Options) {
  const {
    apiUrl,
    threadId,
    assistantInstanceId,
    organizationId,
    userId,
    model,
    initialMessages = [],
    onFinish,
    onError,
  } = options;

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [toolActivities, setToolActivities] = useState<ToolActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setStreamingContent("");
    }
  }, []);

  const sendMessage = useCallback(
    async ({
      content,
      parts,
      toolSelections,
      inputData,
    }: SendMessageOptions) => {
      if (isLoading) {
        console.warn("[useChatV2] Already streaming, ignoring send");
        return;
      }

      // Create user message with parts array (UI expects this format)
      const userParts = parts || [{ type: "text", text: content }];
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        parts: userParts,
        createdAt: new Date(),
      };

      // Add user message to state
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setStreamingContent("");
      setToolActivities([]);

      // Create abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Build payload - send parts as content array (AI SDK v5 format)
      const payload = {
        messages: [...messages, userMessage].map((msg) => ({
          role: msg.role,
          content: msg.parts, // AI SDK v5 uses parts array
        })),
        threadId,
        assistantInstanceId,
        organizationId,
        userId,
        model: model || "default",
        toolSelections,
        inputData,
      };

      console.log("[useChatV2] Sending payload:", payload);

      // Telemetry: log stream start time
      const streamStartTime = performance.now();
      console.log("[useChatV2] Stream started at:", new Date().toISOString());

      try {
        let assistantContent = "";
        const assistantMessageId = `assistant-${Date.now()}`;

        // Stream events from Chat V2
        for await (const event of streamChatV2({
          url: apiUrl,
          payload,
          signal: abortController.signal,
        })) {
          console.log("[useChatV2] Event:", event.event, event.data);

          switch (event.event) {
            case "response.delta":
              // Append text delta to streaming content
              if (event.data?.textDelta) {
                assistantContent += event.data.textDelta;
                setStreamingContent(assistantContent);
              }
              break;

            case "tool_call":
              // Add tool call to activity
              if (event.data?.toolCallId) {
                setToolActivities((prev) => [
                  ...prev,
                  {
                    id: event.data.toolCallId,
                    toolCallId: event.data.toolCallId,
                    toolName: event.data.toolName || "unknown",
                    args: event.data.args,
                    status: "pending",
                  },
                ]);
              }
              break;

            case "tool_result":
              // Update tool activity with result
              if (event.data?.toolCallId) {
                setToolActivities((prev) =>
                  prev.map((activity) =>
                    activity.toolCallId === event.data.toolCallId
                      ? {
                          ...activity,
                          result: event.data.result,
                          status: "completed",
                        }
                      : activity,
                  ),
                );
              }
              break;

            case "tool_error":
              // Update tool activity with error
              if (event.data?.toolCallId) {
                setToolActivities((prev) =>
                  prev.map((activity) =>
                    activity.toolCallId === event.data.toolCallId
                      ? {
                          ...activity,
                          error: event.data.error,
                          status: "error",
                        }
                      : activity,
                  ),
                );
              }
              break;

            case "response.completed":
              // Telemetry: log stream completion time
              const streamEndTime = performance.now();
              const streamDuration = streamEndTime - streamStartTime;
              console.log(
                "[useChatV2] Stream completed at:",
                new Date().toISOString(),
              );
              console.log(
                `[useChatV2] Stream duration: ${streamDuration.toFixed(2)}ms`,
              );

              // Build parts array with tool activities first, then text content
              const assistantParts: any[] = [];

              // Add tool activities as parts (so they appear in message history)
              // Use "tool-{name}" format to match ChatAssistant.tsx filter (line 2046: t.startsWith("tool-"))
              // Map our status to AI SDK state values for ToolHeader
              toolActivities.forEach((activity) => {
                const state =
                  activity.status === "pending"
                    ? "input-available"
                    : activity.status === "error"
                      ? "output-error"
                      : "output-available";

                assistantParts.push({
                  type: `tool-${activity.toolName}`, // Prefix with "tool-" to match rendering filter
                  toolCallId: activity.toolCallId,
                  state: state, // AI SDK state field
                  input: activity.args, // ToolInput expects "input" field
                  result: activity.result, // Tool result
                  output: activity.result, // Some code checks "output" instead
                  errorText: activity.error, // ToolOutput expects "errorText"
                });
              });

              // Add text content
              if (assistantContent) {
                assistantParts.push({ type: "text", text: assistantContent });
              }

              // Finalize assistant message with parts array including tool activities
              const assistantMessage: ChatMessage = {
                id: assistantMessageId,
                role: "assistant",
                parts: assistantParts,
                createdAt: new Date(),
              };

              setMessages((prev) => [...prev, assistantMessage]);
              setStreamingContent("");
              setIsLoading(false);
              abortControllerRef.current = null;

              console.log("[useChatV2] Stream completed", event.data);
              console.log(
                `[useChatV2] Persisted ${toolActivities.length} tool activities to message parts`,
              );
              if (onFinish) {
                onFinish(assistantMessage, event.data?.usage);
              }
              break;
          }
        }
      } catch (error: any) {
        console.error("[useChatV2] Stream error:", error);

        // If not aborted, treat as error
        if (error.name !== "AbortError") {
          // Telemetry: log error time
          const errorTime = performance.now();
          const errorDuration = errorTime - streamStartTime;
          console.log(
            `[useChatV2] Stream failed after ${errorDuration.toFixed(2)}ms`,
          );

          // Create inline error message for better UX
          const errorMessage: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            parts: [
              {
                type: "text",
                text:
                  streamingContent ||
                  "Sorry, I encountered an error while processing your request.",
              },
              {
                type: "error",
                error: error.message || "Unknown error occurred",
              },
            ],
            createdAt: new Date(),
          };

          setMessages((prev) => [...prev, errorMessage]);
          setIsLoading(false);
          setStreamingContent("");
          abortControllerRef.current = null;

          if (onError) {
            onError(error);
          }
        } else {
          // Aborted by user, finalize with partial content
          if (streamingContent) {
            const assistantMessage: ChatMessage = {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              parts: [
                { type: "text", text: streamingContent + " [interrupted]" },
              ],
              createdAt: new Date(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
          }
          setStreamingContent("");
          setIsLoading(false);
        }
      }
    },
    [
      messages,
      isLoading,
      apiUrl,
      threadId,
      assistantInstanceId,
      organizationId,
      userId,
      model,
      onFinish,
      onError,
      streamingContent,
    ],
  );

  return {
    messages,
    setMessages,
    toolActivities,
    isLoading,
    streamingContent,
    sendMessage,
    stop,
  };
}
